<template>
  <div class="sensor-group-sensors-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1>Sensor Group Sensors</h1>
          <p>Gestión de relaciones entre grupos de sensores y sensores</p>
        </v-col>
      </v-row>
      
      <!-- Filtros -->
      <v-row>
        <v-col cols="12" md="6">
          <v-select
            v-model="selectedGroupId"
            :items="groupOptions"
            label="Filtrar por Grupo"
            clearable
            @update:model-value="filterByGroup"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="selectedSensorId"
            :items="sensorOptions"
            label="Filtrar por Sensor"
            clearable
            @update:model-value="filterBySensor"
          ></v-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="sensorGroupSensorsList"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Lista de Sensores por Grupo</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="refreshData">
                  <v-icon left>mdi-refresh</v-icon>
                  Actualizar
                </v-btn>
                <v-btn color="success" class="ml-2" @click="addItem">
                  <v-icon left>mdi-plus</v-icon>
                  Agregar Relación
                </v-btn>
              </v-toolbar>
            </template>
            <template v-slot:item.groupId="{ item }">
              <v-chip color="blue" variant="outlined" small>
                {{ item.groupId }}
              </v-chip>
            </template>
            <template v-slot:item.sensorId="{ item }">
              <v-chip color="green" variant="outlined" small>
                {{ item.sensorId }}
              </v-chip>
            </template>
            <template v-slot:item.position="{ item }">
              <v-chip color="orange" small>
                #{{ item.position }}
              </v-chip>
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn size="small" icon variant="text" @click="moveUp(item)">
                <v-icon>mdi-arrow-up</v-icon>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="moveDown(item)">
                <v-icon>mdi-arrow-down</v-icon>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="editItem(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="deleteItem(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useSensorGroupSensorStore } from './store'
import { SensorGroupSensor } from './models/sensorGroupSensor'

const sensorGroupSensorStore = useSensorGroupSensorStore()
const loading = ref(false)
const selectedGroupId = ref<string | null>(null)
const selectedSensorId = ref<string | null>(null)

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Grupo ID', key: 'groupId' },
  { title: 'Sensor ID', key: 'sensorId' },
  { title: 'Posición', key: 'position' },
  { title: 'Creado', key: 'createdAt' },
  { title: 'Creado por', key: 'createdBy' },
  { title: 'Acciones', key: 'actions', sortable: false },
]

// Opciones para los filtros (en un caso real, estos vendrían de otros stores)
const groupOptions = ref([
  { title: 'Grupo 1', value: 'group1' },
  { title: 'Grupo 2', value: 'group2' },
  { title: 'Grupo 3', value: 'group3' },
])

const sensorOptions = ref([
  { title: 'Sensor A', value: 'sensorA' },
  { title: 'Sensor B', value: 'sensorB' },
  { title: 'Sensor C', value: 'sensorC' },
])

const sensorGroupSensorsList = computed(() => {
  let list = sensorGroupSensorStore.sensorGroupSensors
  
  if (selectedGroupId.value) {
    list = list.filter(item => item.groupId === selectedGroupId.value)
  }
  
  if (selectedSensorId.value) {
    list = list.filter(item => item.sensorId === selectedSensorId.value)
  }
  
  // Ordenar por posición dentro de cada grupo
  return list.sort((a, b) => {
    if (a.groupId === b.groupId) {
      return a.position - b.position
    }
    return a.groupId.localeCompare(b.groupId)
  })
})

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('es-ES')
}

const refreshData = async () => {
  loading.value = true
  try {
    await sensorGroupSensorStore.getSensorGroupSensors()
  } finally {
    loading.value = false
  }
}

const filterByGroup = async (groupId: string | null) => {
  if (groupId) {
    loading.value = true
    try {
      await sensorGroupSensorStore.getSensorGroupSensorsByGroupId(groupId)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterBySensor = async (sensorId: string | null) => {
  if (sensorId) {
    loading.value = true
    try {
      await sensorGroupSensorStore.getSensorGroupSensorsBySensorId(sensorId)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const addItem = () => {
  // Implementar lógica para agregar relación
  console.log('Add new sensor group sensor relation')
}

const editItem = (item: SensorGroupSensor) => {
  // Implementar lógica de edición
  console.log('Edit item:', item)
}

const deleteItem = (item: SensorGroupSensor) => {
  // Implementar lógica de eliminación
  console.log('Delete item:', item)
}

const moveUp = async (item: SensorGroupSensor) => {
  if (item.position > 1) {
    // Lógica para mover hacia arriba
    const newPosition = item.position - 1
    console.log(`Moving sensor ${item.sensorId} up to position ${newPosition}`)
    // Aquí llamarías a la acción updateSensorPositions
  }
}

const moveDown = async (item: SensorGroupSensor) => {
  // Lógica para mover hacia abajo
  const newPosition = item.position + 1
  console.log(`Moving sensor ${item.sensorId} down to position ${newPosition}`)
  // Aquí llamarías a la acción updateSensorPositions
}

onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.sensor-group-sensors-view {
  height: 100%;
}
</style>
